import * as React from "react"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { Autocomplete, Box, IconButton, Stack, TextField, Typography } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close';
import Grid from "@mui/material/Grid2"
import { Button } from "@mui/material"
import { findLocation, useLocations } from "../../ReactQuery/QueryLocations"
import { AXIOS_API_INSTANCE } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from "notistack"
import { useTrackedUser } from "../../UserState"
import { MuiTelInput } from "mui-tel-input"
import { MaterialUISwitchTextEmail } from "../../../Utils/MaterialUISwitch"
import { template_service_communication } from "../../../Utils/service"
import { formatCustomerName, formatPhoneNumber } from "../../../Utils"
import { LoadingBackdropIndeterminate } from "../../LoadingBackdrop"
import AIButton from "../../../Utils/AIButton"

export default function CustomerCommunicationModal({
    open,
    closeModal,
    serviceOrder,
    setServiceOrder,
    setServiceOrderSavingStep,
    status = null,
    allow_ai = false,
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [my_user,] = useTrackedUser();
    const { data: locations, } = useLocations();
    const [sendingMessage, setSendingMessage] = React.useState(false)
    const [customerCommunicationType, setCustomerCommunicationType] = React.useState('text')
    const [customerCommunicationPhone, setCustomerCommunicationPhone] = React.useState('')
    const [customerCommunicationEmail, setCustomerCommunicationEmail] = React.useState('')
    const [customerCommunicationMessage, setCustomerCommunicationMessage] = React.useState('')

    const [aiHistory, setAIHistory] = React.useState([])

    const phones = (serviceOrder.preferred_phone ? [{ value: serviceOrder.preferred_phone, name: "Preferred Contact" }] : []).concat(
        serviceOrder.customer.phone ? [{ value: serviceOrder.customer.phone, name: formatCustomerName(serviceOrder.customer) }] : []
    ).concat(
        (serviceOrder.customer.customer_contacts ?? []).sort((a, b) => (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name)).map((contact) => contact.phone ? { value: contact.phone, name: `${contact.first_name} ${contact.last_name}` } : undefined).filter((x) => x !== undefined)
    )
    const emails = (serviceOrder.preferred_email ? [{ value: serviceOrder.preferred_email, name: "Preferred Contact" }] : []).concat(
        serviceOrder.customer.email ? [{ value: serviceOrder.customer.email, name: formatCustomerName(serviceOrder.customer) }] : []
    ).concat(
        (serviceOrder.customer.customer_contacts ?? []).sort((a, b) => (a.first_name + ' ' + a.last_name).localeCompare(b.first_name + ' ' + b.last_name)).map((contact) => contact.email ? { value: contact.email, name: `${contact.first_name} ${contact.last_name}` } : undefined).filter((x) => x !== undefined)
    )

    const submit = (e) => {
        setSendingMessage(true)
        const customer_communication_object = {
            destination_type: customerCommunicationType,
            destination: customerCommunicationType === 'text' ? customerCommunicationPhone : customerCommunicationEmail,
            message: customerCommunicationMessage
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrder.id}/send_customer_communication`, customer_communication_object).then((serviceOrderNew) => {
                enqueueSnackbar(`Successfully sent ${customer_communication_object.destination_type === 'email' ? 'email' : 'text'} to customer (${customer_communication_object.destination_type === 'email' ? customer_communication_object.destination : formatPhoneNumber(customer_communication_object.destination)})`, { variant: 'success' })
                setSendingMessage(false)
                setServiceOrder(serviceOrderNew.data.data)
                closeModal(true)
            }).catch((error) => {
                setSendingMessage(false)
                enqueueSnackbar(`Failed to send ${customer_communication_object.destination_type === 'email' ? 'email' : 'text'} to customer (${customer_communication_object.destination_type === 'email' ? customer_communication_object.destination : formatPhoneNumber(customer_communication_object.destination)}) <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            }))
    }

    React.useEffect(() => {
        const default_customer_communication_type = ((serviceOrder.preferred_phone && 'text') || (serviceOrder.preferred_email && 'email') || serviceOrder.customer.preferred_communication_type) ?? 'text'
        const service_order_location = findLocation(locations, serviceOrder.location_id);
        const new_message = (default_customer_communication_type === 'email' ? my_user.client.service_order_statuses[status]?.customer_communication_email : my_user.client.service_order_statuses[status]?.customer_communication_text) ?? ''
        setCustomerCommunicationType(default_customer_communication_type)
        setCustomerCommunicationMessage(template_service_communication(service_order_location, serviceOrder, new_message, status ?? ''))
        setCustomerCommunicationPhone((serviceOrder.preferred_phone || serviceOrder.customer.phone) ?? '')
        setCustomerCommunicationEmail((serviceOrder.preferred_email || serviceOrder.customer.email) ?? '')
        setAIHistory([])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
        <div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                maxWidth={'md'}
                fullWidth
            >
                <LoadingBackdropIndeterminate
                    open={sendingMessage}
                    text={"Sending Message"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>Send Message</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Grid size="grow">
                            <Grid container alignItems={"center"} justifyContent="space-between" spacing={2}>
                                <Grid size="auto">
                                    <Stack direction="row" component="label" alignItems="center" justifyContent="flex-begin">
                                        <Typography sx={{ fontWeight: 'bold' }}>Text</Typography>
                                        <MaterialUISwitchTextEmail
                                            onChange={(e) => {
                                                const service_order_location = findLocation(locations, serviceOrder.location_id);
                                                const new_message = (e.target.checked ? my_user.client.service_order_statuses[status]?.customer_communication_email : my_user.client.service_order_statuses[status]?.customer_communication_text) ?? ''
                                                setCustomerCommunicationType(e.target.checked ? 'email' : 'text');
                                                setCustomerCommunicationMessage(template_service_communication(service_order_location, serviceOrder, new_message, status ?? ''))
                                                setAIHistory([])
                                            }}
                                            checked={customerCommunicationType === 'email'}
                                        />
                                        <Typography sx={{ fontWeight: 'bold' }}>Email</Typography>
                                    </Stack>
                                </Grid>
                                <Grid size="grow">
                                    {customerCommunicationType === 'text' &&
                                        <Autocomplete
                                            options={phones}
                                            value={customerCommunicationPhone}
                                            isOptionEqualToValue={(option, value) => option.value === value}
                                            getOptionLabel={(option) => option.value ?? option}
                                            onChange={(event, newValue) => {
                                                setCustomerCommunicationPhone(newValue?.value ?? '');
                                            }}
                                            freeSolo
                                            size="small"
                                            sx={{
                                                '& .MuiFilledInput-root': {
                                                    pt: 0,
                                                    pl: '12px'
                                                },
                                                '& .MuiFilledInput-root .MuiFilledInput-input': {
                                                    pt: '25px',
                                                    pr: '12px',
                                                    pb: '8px',
                                                    pl: '12px',
                                                }
                                            }}
                                            renderInput={(params) => {
                                                return (
                                                    <div ref={params.InputProps.ref}>
                                                        <MuiTelInput
                                                            {...params.inputProps}
                                                            ref={undefined}
                                                            inputRef={params.inputProps.ref}
                                                            className=""
                                                            margin="dense"
                                                            id="phone"
                                                            label="Phone"
                                                            type="tel"
                                                            fullWidth
                                                            variant="filled"
                                                            value={`+1 ${customerCommunicationPhone}`}
                                                            onChange={(event, extra) => {
                                                                if (extra.nationalNumber !== null && extra.nationalNumber !== undefined && extra.nationalNumber.length < 11) {
                                                                    setCustomerCommunicationPhone(extra.nationalNumber)
                                                                }
                                                            }}
                                                            InputLabelProps={{ shrink: true }}
                                                            onlyCountries={['US']}
                                                            defaultCountry="US"
                                                            forceCallingCode
                                                            disableDropdown
                                                        />
                                                    </div>
                                                )
                                            }}
                                            renderOption={(props, option, { inputValue }) => {
                                                const { key, ...optionProps } = props;

                                                return (
                                                    <li key={key} {...optionProps}>
                                                        <div>
                                                            <span>
                                                                {formatPhoneNumber(option.value)}
                                                            </span>
                                                            <br />
                                                            <Typography sx={{ opacity: '50%', fontSize: '.7rem', pl: 1 }}>{option.name}</Typography>
                                                        </div>
                                                    </li>
                                                );
                                            }}
                                        />
                                    }
                                    {customerCommunicationType === 'email' &&
                                        <Autocomplete
                                            options={emails}
                                            value={customerCommunicationEmail}
                                            isOptionEqualToValue={(option, value) => option.value === value}
                                            getOptionLabel={(option) => option.value ?? option}
                                            onChange={(event, newValue) => {
                                                setCustomerCommunicationEmail(newValue?.value ?? '');
                                            }}
                                            freeSolo
                                            renderInput={(params) => {
                                                return (<TextField {...params} label="Email" variant="filled" type="email" fullWidth autoComplete='off' />)
                                            }}
                                            renderOption={(props, option, { inputValue }) => {
                                                const { key, ...optionProps } = props;

                                                return (
                                                    <li key={option.value} {...optionProps}>
                                                        <div>
                                                            <span>
                                                                {option.value}
                                                            </span>
                                                            <br />
                                                            <Typography sx={{ opacity: '50%', fontSize: '.7rem', pl: 1 }}>{option.name}</Typography>
                                                        </div>
                                                    </li>
                                                );
                                            }}
                                        />
                                    }
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        margin="dense"
                                        id="customercommunicationmessage"
                                        label="Message to Customer"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        minRows={2}
                                        value={customerCommunicationMessage}
                                        onChange={(event) => setCustomerCommunicationMessage(event.target.value)}
                                        sx={{ mt: 0 }}
                                    />
                                </Grid>
                                {allow_ai &&
                                    <Grid container size={12} justifyContent={'flex-end'}>
                                        <Grid size="auto" sx={{ pr: 1, pl: 1 }}>
                                            <AIButton
                                                make_prompt={() => {
                                                    const context = {
                                                        "Customer Item": `${serviceOrder.customer_item.manufacturer} ${serviceOrder.customer_item.model}`,
                                                        "Status": serviceOrder.status_name,
                                                        "Drop Off Notes": serviceOrder.customer_notes,
                                                        "Service Order Notes": serviceOrder.service_order_notes.map((note) => note.text),
                                                        "Services To Be Performed": serviceOrder.service_order_services.map((service) => {
                                                            return {
                                                                name: service.service_name,
                                                                summary: service.summary_notes,
                                                                status: service.status,
                                                                approval: service.customer_approval_status,
                                                            }
                                                        }),
                                                    }
                                                    if (customerCommunicationType === 'text') {
                                                        return { prompt: `Generate a text message to ${formatCustomerName(serviceOrder.customer)} for service order #${serviceOrder.id} updating the customer on the latest status of the service order. Do not ask the customer for anything. The context in json format is:\n${JSON.stringify(context, null, 2)}` }
                                                    } else {
                                                        return { prompt: `Generate an email to ${formatCustomerName(serviceOrder.customer)} for service order #${serviceOrder.id} updating the customer on the latest status of the service order. Do not include a subject. Do not ask the customer for anything, do not include signature, and do not include a greeting. The context in json format is:\n${JSON.stringify(context, null, 2)}` }
                                                    }
                                                }}
                                                prompt_history={aiHistory}
                                                use_markdown={false}
                                                callback={(response) => {
                                                    setCustomerCommunicationMessage(response.output.content[0].text)
                                                    setAIHistory([...aiHistory, response.input, response.output])
                                                }}
                                            >
                                                {aiHistory.length === 0 ? (customerCommunicationType === 'text' ? 'Generate Text Message' : 'Generate Email') : 'Refine Message'}
                                            </AIButton>
                                        </Grid>
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid xs="auto">
                        </Grid>
                        <Grid xs="auto">
                            <Button variant="contained" type="submit" onClick={submit}>
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div>
    )
}
